/**
 * SearchInput — textové vyhledávání pro ACM Dashboard.
 *
 * Použití:
 *   <SearchInput value={q} onChange={setQ} placeholder="hledat hostitele…" />
 *   <SearchInput value={q} onChange={setQ} size="md" className="w-72" />
 *
 * size:
 *   sm  — py-1.5 text-xs  (v hlavičce karty, vedle tlačítek)
 *   md  — py-2   text-sm  (standalone, nad tabulkou)
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FIELD_SURFACE } from './Input';

export type SearchSize = 'sm' | 'md';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: SearchSize;
  className?: string;
  autoFocus?: boolean;
  /** Zobrazit počet výsledků vpravo uvnitř pole */
  resultCount?: number;
  totalCount?: number;
}

const SIZE: Record<SearchSize, { wrap: string; icon: number; input: string; clear: number }> = {
  sm: { wrap: 'h-8', icon: 13, input: 'pl-8  pr-7 py-1.5 text-xs', clear: 12 },
  md: { wrap: 'h-10', icon: 15, input: 'pl-10 pr-8 py-2   text-sm', clear: 14 },
};

export function SearchInput({
  value,
  onChange,
  placeholder,
  size = 'sm',
  className,
  autoFocus,
  resultCount,
  totalCount,
}: SearchInputProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.search', 'Hledat…');
  const inputRef = useRef<HTMLInputElement>(null);
  const s = SIZE[size];
  const hasResult = value && resultCount !== undefined && totalCount !== undefined;

  return (
    <div className={cn('relative flex items-center group', s.wrap, className)}>
      {/* Ikona lupy */}
      <Search
        size={s.icon}
        className={cn(
          'absolute left-2.5 pointer-events-none transition-colors duration-150',
          value ? 'text-plex' : 'text-gray-500 group-focus-within:text-plex',
        )}
      />

      {/* Input */}
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={resolvedPlaceholder}
        autoFocus={autoFocus}
        className={cn(
          'w-full rounded-lg transition-all duration-150',
          FIELD_SURFACE,
          s.input,
          hasResult ? 'pr-16' : '',
        )}
      />

      {/* Counter výsledků */}
      {hasResult && (
        <span
          className={cn(
            'absolute right-7 text-[10px] font-mono pointer-events-none',
            resultCount === 0 ? 'text-red-400' : 'text-gray-500',
          )}
        >
          {resultCount}/{totalCount}
        </span>
      )}

      {/* Clear button */}
      {value && (
        <button
          type='button'
          onClick={() => {
            onChange('');
            inputRef.current?.focus();
          }}
          className={cn(
            'absolute right-2 rounded-md transition-colors',
            'text-gray-500 hover:text-gray-200 hover:bg-gray-700 p-0.5',
          )}
          tabIndex={-1}
          aria-label={t('ui.clearSearch', 'Vymazat hledání')}
        >
          <X size={s.clear} />
        </button>
      )}
    </div>
  );
}
